import { api, getApiBase, getToken } from "../api";
import { platformLabel } from "../platforms";

const CREDENTIAL_FIELDS: Record<string, { key: string; label: string; multiline?: boolean }[]> = {
  wordpress: [{key:"siteUrl",label:"Existing WordPress site URL (HTTPS)"},{key:"username",label:"WordPress publisher username"},{key:"applicationPassword",label:"Application password (not your login password)"}],
  webflow: [{key:"apiToken",label:"Site token with CMS read/write access"},{key:"siteId",label:"Existing Webflow site ID"},{key:"collectionId",label:"Existing article collection ID"},{key:"bodyField",label:"Rich-text field slug"},{key:"publicCollectionUrl",label:"Published collection URL, e.g. https://example.com/blog"}],
  wix_blog: [{key:"apiKey",label:"Wix API key with Manage Blog and Read Members"},{key:"siteId",label:"Existing Wix site ID"},{key:"memberId",label:"Approved author member ID (existing site member)"},{key:"siteUrl",label:"Published Wix site URL (include its site path)"}],
  cloudflare_pages: [
    { key: "apiToken", label: "Cloudflare API Token (Workers Scripts: Edit, Account Settings: Read)" },
    { key: "accountId", label: "Cloudflare Account ID" },
  ],
  netlify: [{ key: "accessToken", label: "Netlify Personal Access Token" }],
  github_pages: [
    // FIXED 2026-09-02: label corrected on both fields before Kalraj's first
    // real setup. "workflow" scope was never accurate, the adapter only uses
    // the Contents + Pages REST APIs, never Actions/workflow files. "or org"
    // was never accurate either: deploy() creates repos via POST /user/repos,
    // which always creates on the token owner's PERSONAL account regardless
    // of what's typed here, so an org name here would silently create the
    // repo somewhere other than where every other call in the adapter looks
    // for it. Org support would need POST /orgs/{org}/repos instead, not
    // built. Personal account only until that's added.
    { key: "token", label: "GitHub Personal Access Token (repo scope)" },
    { key: "owner", label: "GitHub username (personal account only, not an org)" },
  ],
  telegraph: [{ key: "accessToken", label: "Telegraph access_token (create once, see note below)" }],
  medium: [
    { key: "integrationToken", label: "Medium Integration Token" },
    { key: "canonicalUrl", label: "Canonical URL (optional)" },
  ],
  devto: [
    { key: "apiKey", label: "Dev.to API Key" },
    { key: "canonicalUrl", label: "Canonical URL (optional)" },
  ],
  bluesky: [
    { key: "handle", label: "Bluesky handle (e.g. nvscustoms.bsky.social)" },
    { key: "appPassword", label: "App password (Settings > App Passwords, never the real login password)" },
  ],
  internet_archive: [
    { key: "accessKey", label: "Access Key (archive.org/account/s3.php)" },
    { key: "secretKey", label: "Secret Key" },
  ],
  mastodon: [
    { key: "instance", label: "Instance (e.g. mastodon.social)" },
    { key: "accessToken", label: "Access Token (create at <instance>/settings/applications)" },
  ],
  telegram: [{ key: "botToken", label: "Bot Token (from @BotFather)" }],
  eventbrite: [
    { key: "privateToken", label: "Private Token (Account Settings > Developer Links)" },
    { key: "organizationId", label: "Organization ID (optional, auto-detected if blank)" },
  ],
  beehiiv: [
    { key: "apiKey", label: "API Key" },
    { key: "publicationId", label: "Publication ID (starts with pub_)" },
  ],
  zenodo: [{ key: "accessToken", label: "Personal Access Token (deposit:write scope)" }],
  raindrop: [
    { key: "testToken", label: "Test Token (Settings > Integrations > For Developers)" },
    { key: "targetUrl", label: "Default URL to bookmark (usually the client's money site)" },
  ],
  facebook_pages: [{ key: "pageId", label: "Page ID (shown after connecting; edit here to switch Pages)" }],
  aws_s3: [
    { key: "accessKeyId", label: "AWS Access Key ID (IAM user)" },
    { key: "secretAccessKey", label: "AWS Secret Access Key" },
    { key: "region", label: "Region (e.g. us-east-1)" },
  ],
  azure_storage: [
    { key: "accountName", label: "Storage Account Name" },
    { key: "accountKey", label: "Storage Account Key" },
    { key: "websiteUrl", label: "Website URL (paste from Azure Portal > Static website, after first deploy)" },
  ],
  firebase_hosting: [
    { key: "serviceAccountJson", label: "Service Account JSON (paste the entire downloaded key file)", multiline: true },
  ],
  digitalocean_spaces: [
    { key: "accessKeyId", label: "Spaces Access Key" },
    { key: "secretAccessKey", label: "Spaces Secret Key" },
    { key: "region", label: "Region (e.g. nyc3, sfo3, ams3, sgp1, fra1)" },
  ],
  vultr_object_storage: [
    { key: "accessKeyId", label: "Access Key" },
    { key: "secretAccessKey", label: "Secret Key" },
    { key: "hostname", label: "Hostname (from the subscription's S3 Credentials, e.g. ewr1.vultrobjects.com)" },
  ],
  wasabi: [
    { key: "accessKeyId", label: "Access Key" },
    { key: "secretAccessKey", label: "Secret Key" },
    { key: "region", label: "Region (e.g. us-east-1, eu-central-1 — see wasabi.com/company/storage-regions)" },
  ],
  linode: [
    { key: "accessKeyId", label: "Access Key" },
    { key: "secretAccessKey", label: "Secret Key" },
    { key: "region", label: "Region (e.g. us-iad, us-ord, eu-central-1 — Object Storage tab in the Linode/Akamai Cloud Manager)" },
  ],
  oracle_object_storage: [
    { key: "accessKeyId", label: "Customer Secret Key — Access Key" },
    { key: "secretAccessKey", label: "Customer Secret Key — Secret Key" },
    { key: "region", label: "Region (e.g. us-ashburn-1)" },
    { key: "namespace", label: "Object Storage Namespace (Console > Object Storage > Buckets, shown at the top)" },
  ],
  backblaze_b2: [
    { key: "accessKeyId", label: "Application Key ID" },
    { key: "secretAccessKey", label: "Application Key" },
    { key: "region", label: "Region (copy exactly from the B2 account page, e.g. us-east-005)" },
  ],
  bunny: [
    { key: "accountApiKey", label: "Account API Key (Bunny Dashboard > Account Settings > API Key)" },
    { key: "region", label: "Primary storage region code, optional (DE, NY, LA, or SG — defaults to DE)" },
  ],
  ibm_cos: [
    { key: "accessKeyId", label: "HMAC Access Key ID (Service Credentials, with \"Include HMAC Credential\" on)" },
    { key: "secretAccessKey", label: "HMAC Secret Access Key" },
    { key: "region", label: "Region (e.g. us-south, eu-de)" },
  ],
  ovhcloud_object_storage: [
    { key: "accessKeyId", label: "Access Key" },
    { key: "secretAccessKey", label: "Secret Key" },
    { key: "region", label: "Region code (e.g. gra, sbg, bhs — Object Storage > Users in the OVHcloud Control Panel)" },
  ],
  upcloud_object_storage: [
    { key: "accessKeyId", label: "Access Key" },
    { key: "secretAccessKey", label: "Secret Key" },
    { key: "hostname", label: "Object Storage instance hostname (e.g. abcd1.upcloudobjects.com — see note below, must be created first)" },
  ],
  scaleway_object_storage: [
    { key: "accessKeyId", label: "API Key — Access Key" },
    { key: "secretAccessKey", label: "API Key — Secret Key" },
    { key: "region", label: "Region (fr-par, nl-ams, pl-waw, or it-mil)" },
  ],
};

const PLATFORM_NOTES: Record<string, string> = {
  wordpress: "Publishes approved articles on this existing site. Updates and withdrawals require explicit confirmation; withdrawal returns the post to draft. The publisher must have publish_posts permission. Whole-site template upload is unsupported.",
  webflow: "Publishes into an existing collection with name, slug and one rich-text field. Additional required fields and whole-site template upload are unsupported. The source URL uses your published collection URL and the returned item slug; check the public page before treating links as observed. Does not publish unrelated staged site changes.",
  wix_blog: "Publishes approved text and links to this existing blog with the selected author. Image import, member creation, whole-site template upload, update and withdrawal are unsupported. It reads the published post after creation; a timeout stays uncertain and must be inspected before another write.",
  telegraph: `Telegraph needs one access_token per agency, created once via their public
    <code>createAccount</code> endpoint. This dashboard does not create it for you.
    Create it yourself (e.g. <code>curl "https://api.telegra.ph/createAccount?short_name=ShopPositioner"</code>)
    and paste the returned token above.`,
  cloudflare_pages: `Deploys via Cloudflare Workers static assets, giving a
    <code>&lt;slug&gt;.&lt;your-subdomain&gt;.workers.dev</code> URL (not pages.dev).
    Cloudflare's own current docs recommend Workers over Pages for new projects.
    The token needs <code>Workers Scripts: Edit</code>, not Pages permissions.`,
  mastodon: `Per-client: pick a client above first. The access token is created directly at
    <code>&lt;instance&gt;/settings/applications</code>, no OAuth redirect needed.`,
  telegram: `The bot must be added as an <strong>ADMIN</strong> of the target channel before
    it can post there, or every send fails regardless of a correct token. When deploying,
    the client's "slug" for this platform should be the channel's @username.`,
  facebook_pages: `Uses Meta's app in Development Mode: works without App Review as long as the
    Facebook user completing this has a role (Admin/Developer/Tester) on the CloudPush Meta app -
    add them in the Meta Developer dashboard first. Click Connect via Facebook, log in, pick the
    Page when prompted (or it defaults to the first one and you can edit the Page ID above after).`,
  aws_s3: `Serves over HTTP only, not HTTPS, unless a CloudFront distribution is added later
    (not built yet). Uses an IAM user's access keys, not the AWS root account. Needs
    s3:CreateBucket, s3:PutBucketPolicy, s3:PutBucketWebsite, s3:PutBucketPublicAccessBlock,
    and s3:PutObject permissions on the IAM user.`,
  azure_storage: `Needs the Website URL from the Azure Portal (Storage Account > Static website)
    because that hostname includes a segment Azure assigns that can't be predicted in advance.
    Deploy once first, then check the portal, then paste the URL back here and save.
    This adapter's signing has less independent verification than the others (see the code
    comment in azure-sharedkey.ts) since Microsoft doesn't publish a test vector the way AWS does -
    worth a careful first live test.`,
  firebase_hosting: `Needs a Google Cloud service account with the <strong>Firebase Hosting Admin</strong> role,
    with the Firebase Hosting API enabled on the project. Download its JSON key and paste the
    <strong>whole file</strong> above, unedited. Gives each client a real <code>&lt;slug&gt;.web.app</code>
    subdomain with free HTTPS, no separate CDN step needed the way AWS S3 needs CloudFront.`,
  digitalocean_spaces: `<strong>Least verified adapter in this dashboard</strong> — DigitalOcean's docs never assemble one
    complete worked example, this was built from three separate corroborating sources instead of one authoritative
    one. Treat the first deploy as a real test. Serves over HTTPS at a <code>&lt;slug&gt;.&lt;region&gt;-static.
    digitaloceanspaces.com</code> address specifically — DO's own docs say index-page auto-serving does NOT work
    through their CDN or a custom domain, only on this exact "-static" hostname. Create the key under
    Spaces Object Storage &gt; Access Keys in the DigitalOcean control panel, Full Access scope (not limited to
    specific buckets — this needs to create a new bucket per client).`,
  vultr_object_storage: `Uses per-object public-read ACLs, not a bucket policy — different from every other
    adapter here, matched to how Vultr's own guides grant public access. <strong>Unconfirmed:</strong> the bucket
    root serving index.html automatically is confirmed in Vultr's current docs, but nested paths (every blog
    article on this platform lives at <code>blog/&lt;slug&gt;/</code>, not the root) are not — the first deploy's
    articles are the real test of that. Unlike the other providers here, keys aren't account-wide: Vultr requires
    creating a paid Object Storage subscription first (Products &gt; Cloud Storage &gt; Object Storage &gt; Add
    Object Storage, pick a region), then that subscription's Overview tab has an S3 Credentials section with the
    Access Key and Secret Key (region is whatever region you picked for the subscription).`,
  wasabi: `No static website hosting exists on Wasabi at all, confirmed in writing in their own S3 API
    reference ("we do not expect to support website operations") — every page here serves from an explicit
    <code>/index.html</code> path, same fix as Vultr. <strong>Watch for this on the first deploy:</strong> Wasabi
    blocks self-service public bucket access for accounts created after March 13, 2023 — if bucket creation
    succeeds but the public-read policy step fails, that account-age gate (not a bug here) is almost certainly
    why, and Wasabi Support has to enable it manually first.`,
  linode: `Akamai/Linode's native static website hosting only exists on "legacy" E0/E1 endpoints, not the
    current E2/E3 generation all new capacity goes on — since plain bucket creation doesn't let this adapter see
    or choose which generation a bucket lands on, native website hosting is skipped entirely in favor of explicit
    <code>/index.html</code> paths (bucket policies, which this DOES use, aren't gated by endpoint generation).
    Access keys are created under Object Storage in the Akamai Cloud Manager, account-wide, no per-bucket
    subscription step like Vultr needs.`,
  oracle_object_storage: `<strong>The hardest provider connected tonight.</strong> Oracle's S3-Compatible API has
    no bucket-policy support and no ACL support at all — nothing in this adapter can make a bucket public. Every
    deploy will succeed (bucket created, files uploaded) but come back with a warning: you must open the OCI
    Console for that bucket and set Visibility to "Public" by hand, once per client/satellite, or the site stays
    unreachable. No static website hosting exists either (explicit <code>/index.html</code> paths used
    throughout). Needs a 4th field none of the others do — the Object Storage <strong>Namespace</strong>, shown at
    the top of Console &gt; Object Storage &gt; Buckets — because OCI's endpoint hostname is namespace-specific.`,
  backblaze_b2: `No static website hosting exists on B2's S3-Compatible API at all — Backblaze's own guide for
    serving a home page requires bolting on an external Cloudflare Transform Rule instead. Every page here serves
    from an explicit <code>/index.html</code> path. Public access is a bucket-wide public-read toggle, not a JSON
    policy — B2 has no bucket policies or IAM roles on this API. Create an Application Key under Account &gt;
    Application Keys with read+write access to all buckets (a key scoped to one bucket can't create new ones for
    new clients).`,
  bunny: `<strong>Structurally different from every other adapter here</strong> — Bunny's real API is a plain
    <code>AccessKey</code> header, not S3/SigV4. First deploy for a client creates a Storage Zone AND a Pull Zone
    (Bunny has no single "make it public" toggle, the Pull Zone IS the public CDN in front of the private
    storage) and caches the zone's auto-generated password for reuse on every later deploy. No per-folder
    homepage support exists on Bunny at all (only one global catch-all file for the whole zone), so every page
    here serves from an explicit <code>/index.html</code> path — this is unavoidable on Bunny, not a choice.
    <strong>Least battle-tested adapter added tonight</strong> — treat the first real deploy as the actual test.
    Only the Account API Key is needed here; the per-zone password is generated and stored automatically.`,
  ibm_cos: `IBM's own documentation directly contradicts itself on static website hosting — one page describes a
    full native mechanism, a more recently updated page (checked live, dated 2026-08-13) says plainly "doesn't
    support automatic static website hosting." Given that contradiction, this adapter doesn't rely on it either
    way — explicit <code>/index.html</code> paths throughout. Public access uses the <code>public-read</code>
    canned ACL (the only ACL value IBM's S3 API accepts; IBM has no JSON bucket policies at all). Generate
    credentials under the Object Storage instance's <strong>Service Credentials</strong>, with "Include HMAC
    Credential" switched ON — it's off by default and the resulting key won't work here without it.`,
  ovhcloud_object_storage: `OVHcloud has two different Object Storage products under similar names — this
    adapter targets the current-generation one (<code>s3.&lt;region&gt;.io.cloud.ovh.net</code>), not the older
    "Object Storage Swift" line. Native static website hosting exists but OVHcloud's own docs say its endpoint
    "does not support HTTPS" by default, so this adapter deploys to the regular (HTTPS) bucket endpoint instead
    and uses explicit <code>/index.html</code> paths. Bucket policies aren't supported on this product at all
    (canned public-read ACLs are used instead). If a brand-new client's first deploy gets an occasional 503
    SLOWDOWN error, that's OVHcloud's backend sharding a fresh bucket under load — retrying the publish a minute
    later is the documented fix, not a real failure.`,
  upcloud_object_storage: `<strong>Needs one manual setup step before this adapter can be used at all:</strong>
    UpCloud's endpoint hostname is a random string assigned per Object Storage instance (e.g.
    <code>abcd1.upcloudobjects.com</code>), not derivable from a region code — create the instance once yourself
    (UpCloud Control Panel &gt; Object Storage &gt; + Configure Object Storage), then inside it go to Users &gt;
    Add user, attach both the ECSS3 and IAM policies, and generate the Access key shown there. Paste that
    instance's hostname and the resulting keys above. Native static website hosting exists and is unusually
    well-documented for nested folders, but it needs a separate UpCloud account-level API this adapter was never
    able to confirm the auth for — skipped in favor of explicit <code>/index.html</code> paths.`,
  scaleway_object_storage: `Uses path-style URLs on purpose (Scaleway itself prefers virtual-hosted-style, but
    warns it breaks HTTPS for any bucket name containing a dot). Native Bucket Website hosting exists at a
    separate hostname; root-level index serving is confirmed but nested paths (every blog article here) are only
    strongly implied by an official tutorial, never stated outright — skipped in favor of explicit
    <code>/index.html</code> paths, same caution as Vultr. Generate the API key under IAM &amp; API Keys in the
    Scaleway console, and tick the option that scopes it for Object Storage.`,
};

// Platforms needing an OAuth redirect instead of pasted credentials. Each
// needs a `startPath` (called with ?clientId=... to get the authorize URL)
// and requires a client to be selected, since Facebook Pages (and anything
// else added here later) is always a per-client connection.
const OAUTH_PLATFORMS: Record<string, { startPath: string }> = {
  facebook_pages: { startPath: "/v1/oauth/facebook/start" },
};

// Rewritten 2026-09-03 (UI/UX pass). What changed and why:
//
// Before: every platform (30 of them, once the 9 new clouds landed) rendered
// as an always-expanded .card with its full credential form and a paragraph
// of notes visible whether connected or not — a 7000+px page nobody could
// scan, the single biggest concrete complaint that triggered this pass.
//
// Now: platforms are grouped under their real `category` (Cloud hosts /
// Content platforms, straight from the backend — no second hand-kept list),
// collapsed into one-line rows by default (label, scope tag, connected
// status), and a search box filters by label or key. Clicking a row expands
// it in place to reveal the exact same form/notes/button that always
// existed — none of the connect/OAuth/patch logic below changed, only how
// much of it is visible before you ask for it.
export async function connectionsView(root: HTMLElement) {
  root.innerHTML = `
    <div class="topbar"><h1>Connections</h1></div>
    <div class="card">
      <label>Client (required for per-client platforms; leave blank to manage agency-wide ones only)</label>
      <select id="client-select"><option value="">Agency-wide only</option></select>
    </div>
    <div class="search-box"><input id="platform-search" placeholder="Filter platforms by name…" /></div>
    <div id="summary" class="muted" style="margin:-8px 0 16px"></div>
    <div id="content">Loading…</div>
  `;
  const content = root.querySelector("#content")!;
  const clientSelect = root.querySelector<HTMLSelectElement>("#client-select")!;
  const searchInput = root.querySelector<HTMLInputElement>("#platform-search")!;
  const summaryEl = root.querySelector("#summary")!;

  try {
    const { clients } = await api<{ clients: any[] }>("/v1/clients");
    for (const cl of clients) {
      const opt = document.createElement("option");
      opt.value = cl.id;
      opt.textContent = cl.business_name;
      clientSelect.appendChild(opt);
    }
  } catch {
    // Non-fatal: agency-wide connections still work without the client list.
  }

  function renderRow(p: any, connections: any[], clientId: string | null): string {
    const isClientScoped = p.scope === "client";
    const label = platformLabel(p.platform);
    const scopeTag = `<span class="tag">${p.scope}</span>`;
    const syntheticHost = p.platform === "local_synthetic_host";

    if (isClientScoped && !clientId) {
      return `
        <div class="accordion" data-platform="${p.platform}">
          <button type="button" class="accordion-head">
            <span class="chevron">▸</span>
            <span class="title">${label}</span>
            ${scopeTag}
            <span class="subtitle">Select a client above to connect this platform</span>
            <span class="pill neutral">needs client</span>
          </button>
          <div class="accordion-body" hidden>
            <p class="muted">This platform connects per client. Pick a client from the dropdown above, then come back here.</p>
          </div>
        </div>`;
    }

    const conn = connections.find((c: any) => c.platform === p.platform && (isClientScoped ? c.client_id === clientId : true));
    const fields = CREDENTIAL_FIELDS[p.platform] ?? [];
    const isOAuth = p.platform in OAUTH_PLATFORMS;

    return `
      <div class="accordion" data-platform="${p.platform}">
        <button type="button" class="accordion-head">
          <span class="chevron">▸</span>
          <span class="title">${label}</span>
          ${scopeTag}
          <span class="subtitle">${conn ? conn.identity ?? (conn.status === "connected" ? "Connection tested" : "Saved connection") : "Not connected"}</span>
          <span class="pill ${conn?.status === "connected" ? "ok" : conn ? "warn" : "neutral"}">${conn ? conn.status === "connected" ? "tested" : `saved · ${conn.status}` : "not connected"}</span>
          ${p.category === "cloud_host" ? `<span class="pill ${p.capabilities?.artifacts ? "ok" : "neutral"}">${syntheticHost ? "local synthetic test host" : p.capabilities?.artifacts ? "complete-template publishing" : "template publishing unavailable"}</span>` : ""}
        </button>
        <div class="accordion-body" hidden>
          ${
            isOAuth
              ? `<button class="oauth-btn" data-platform="${p.platform}" style="margin:14px 0 10px">${conn ? "Reconnect via Facebook" : "Connect via Facebook"}</button>`
              : `<div style="height:14px"></div>`
          }
          <div class="grid cols-2">
            ${fields
              .map((f) =>
                f.multiline
                  ? `<div style="grid-column:1/-1"><label>${f.label}</label><textarea data-field="${f.key}" rows="6" style="width:100%;font-family:monospace;font-size:12px" placeholder="${conn ? "already set, leave blank to keep" : ""}"></textarea></div>`
                  : `<div><label>${f.label}</label><input data-field="${f.key}" type="${isOAuth ? "text" : "password"}" placeholder="${conn ? "already set, leave blank to keep" : ""}" /></div>`,
              )
              .join("")}
          </div>
          ${PLATFORM_NOTES[p.platform] ? `<p class="muted-note">${PLATFORM_NOTES[p.platform]}</p>` : ""}
          ${p.category === "cloud_host" ? `<p class="muted-note">${syntheticHost ? "This is a local-only synthetic test destination. It writes no external provider and is excluded from the real provider capability totals." : p.capabilities?.artifacts ? "This adapter can publish a complete template build. A saved and tested connection still requires an explicit publication action from a website workspace." : "Credentials can be saved and tested for supported operations. This adapter does not publish complete template builds; no template publication action is available."}</p>` : ""}
          <div class="connect-err"></div>
          ${
            isOAuth
              ? conn && fields.length
                ? `<button class="patch-btn" data-conn-id="${conn.id}" style="margin-top:10px">Save Page ID</button>`
                : ""
              : `<button class="connect-btn" data-platform="${p.platform}" style="margin-top:10px">${conn ? "Update & test" : "Connect"}</button>`
          }
          ${conn ? `<button type="button" class="test-connection-btn" data-conn-id="${conn.id}" data-platform="${p.platform}" style="margin:10px 0 0 8px">Test saved connection</button>` : ""}
        </div>
      </div>`;
  }

  function renderSection(title: string, platforms: any[], connections: any[], clientId: string | null): string {
    if (platforms.length === 0) return "";
    const testedCount = platforms.filter((p) => connections.some((c: any) => c.platform === p.platform && c.status === "connected" && (p.scope === "client" ? c.client_id === clientId : true))).length;
    const savedCount = platforms.filter((p) => connections.some((c: any) => c.platform === p.platform && c.status !== "connected" && (p.scope === "client" ? c.client_id === clientId : true))).length;
    return `
      <div class="platform-section" data-section>
        <div class="section-header">
          <h3>${title}</h3>
          <span class="count">${testedCount} of ${platforms.length} tested${savedCount ? ` · ${savedCount} saved, not tested` : ""}</span>
        </div>
        ${platforms.map((p) => renderRow(p, connections, clientId)).join("")}
      </div>`;
  }

  async function load() {
    const clientId = clientSelect.value || null;
    const query = clientId ? `?clientId=${clientId}` : "";
    const { connections, availablePlatforms } = await api<{ connections: any[]; availablePlatforms: any[] }>(`/v1/connections${query}`);

    const cloudHosts = availablePlatforms.filter((p: any) => p.category === "cloud_host");
    const contentPlatforms = availablePlatforms.filter((p: any) => p.category === "content_platform");
    const connectionMatches = (p: any, testedOnly: boolean) => connections.some((c: any) => c.platform === p.platform && (!testedOnly || c.status === "connected") && (p.scope === "client" ? c.client_id === clientId : true));
    const totalTested = availablePlatforms.filter((p: any) => connectionMatches(p, true)).length;
    const totalSaved = availablePlatforms.filter((p: any) => connections.some((c: any) => c.platform === p.platform && c.status !== "connected" && (p.scope === "client" ? c.client_id === clientId : true))).length;
    const realCloudHosts = cloudHosts.filter((p: any) => p.platform !== "local_synthetic_host");
    const templateHosts = realCloudHosts.filter((p: any) => p.capabilities?.artifacts === true).length;
    summaryEl.textContent = `${totalTested} of ${availablePlatforms.length} platform connections tested${totalSaved ? ` · ${totalSaved} saved but not tested` : ""} · ${templateHosts} of ${realCloudHosts.length} real cloud hosts support complete-template publishing${clientId ? "" : " (agency-wide view)"}. The local synthetic test host is excluded. Live publication is a separate action from a website workspace.`;

    content.innerHTML =
      renderSection("Cloud hosts (full sites)", cloudHosts, connections, clientId) +
      renderSection("Content platforms", contentPlatforms, connections, clientId);

    // Accordion open/close. A real <button> for the head means this is
    // already keyboard-reachable and Enter/Space-activated for free.
    content.querySelectorAll<HTMLButtonElement>(".accordion-head").forEach((head) => {
      head.onclick = () => {
        const acc = head.closest(".accordion")!;
        const body = acc.querySelector<HTMLElement>(".accordion-body")!;
        body.hidden = !body.hidden;
        acc.classList.toggle("open", !body.hidden);
      };
    });

    content.querySelectorAll<HTMLButtonElement>(".oauth-btn").forEach((btn) => {
      btn.onclick = async () => {
        const platform = btn.dataset.platform!;
        const { startPath } = OAUTH_PLATFORMS[platform];
        try {
          const res = await fetch(`${getApiBase()}${startPath}?clientId=${clientId}`, {
            headers: getToken() ? { Authorization: `Bearer ${getToken()}` } : { "X-CloudPush-Session": "1" },
            credentials: "same-origin",
          });
          const json = (await res.json()) as { authorizeUrl?: string; error?: string };
          if (!json.authorizeUrl) throw new Error(json.error ?? "Could not start the OAuth flow.");
          window.open(json.authorizeUrl, "_blank", "width=600,height=700");
        } catch (e) {
          alert((e as Error).message);
        }
      };
    });

    content.querySelectorAll<HTMLButtonElement>(".patch-btn").forEach((btn) => {
      btn.onclick = async () => {
        const acc = btn.closest(".accordion")!;
        const fields: Record<string, string> = {};
        acc.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>("input[data-field], textarea[data-field]").forEach((input) => {
          if (input.value) fields[input.dataset.field!] = input.value;
        });
        const errEl = acc.querySelector(".connect-err")!;
        try {
          btn.disabled = true;
          btn.textContent = "Saving…";
          await api(`/v1/connections/${btn.dataset.connId}`, { method: "PATCH", body: JSON.stringify({ fields }) });
          await load();
        } catch (e) {
          errEl.innerHTML = `<div class="error-banner">${(e as Error).message}</div>`;
          btn.disabled = false;
          btn.textContent = "Save Page ID";
        }
      };
    });

    content.querySelectorAll<HTMLButtonElement>(".connect-btn").forEach((btn) => {
      btn.onclick = async () => {
        const acc = btn.closest(".accordion")!;
        const platform = btn.dataset.platform!;
        const credentials: Record<string, string> = {};
        acc.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>("input[data-field], textarea[data-field]").forEach((input) => {
          if (input.value) credentials[input.dataset.field!] = input.value;
        });
        const errEl = acc.querySelector(".connect-err")!;
        try {
          btn.disabled = true;
          btn.textContent = "Saving…";
          await api("/v1/connections", { method: "POST", body: JSON.stringify({ clientId, platform, credentials }) });
          await load();
        } catch (e) {
          errEl.innerHTML = `<div class="error-banner">${(e as Error).message}</div>`;
          btn.disabled = false;
          btn.textContent = "Connect";
        }
      };
    });

    content.querySelectorAll<HTMLButtonElement>(".test-connection-btn").forEach((btn) => {
      btn.onclick = async () => {
        const errEl = btn.closest(".accordion")!.querySelector<HTMLElement>(".connect-err")!;
        try {
          btn.disabled = true;
          btn.textContent = "Testing saved connection…";
          const result = await api<{ ok: boolean }>(`/v1/connections/${btn.dataset.connId}/test`, { method: "POST" });
          await load();
          if (!result.ok) {
            const row = content.querySelector<HTMLElement>(`.accordion[data-platform="${btn.dataset.platform}"]`);
            const message = row?.querySelector<HTMLElement>(".connect-err");
            if (message) message.textContent = "The saved provider check failed. Credentials were not changed. Review the provider-specific reconnect fields below.";
          }
        } catch {
          btn.disabled = false;
          btn.textContent = "Test saved connection";
          errEl.textContent = "The saved provider check could not be completed. Credentials were not changed.";
        }
      };
    });

    applyFilter();
  }

  // Search filters by label or raw platform key, live on every keystroke.
  // Hides non-matching rows, and hides a whole section header if nothing in
  // it matches, rather than leaving an empty "Cloud hosts (0 of 17)" group
  // sitting above nothing.
  function applyFilter() {
    const term = searchInput.value.trim().toLowerCase();
    let anyVisible = false;
    content.querySelectorAll<HTMLElement>(".platform-section").forEach((section) => {
      let sectionHasMatch = false;
      section.querySelectorAll<HTMLElement>(".accordion").forEach((acc) => {
        const platform = acc.dataset.platform ?? "";
        const matches = !term || platformLabel(platform).toLowerCase().includes(term) || platform.includes(term);
        acc.hidden = !matches;
        if (matches) sectionHasMatch = true;
      });
      section.hidden = !sectionHasMatch;
      if (sectionHasMatch) anyVisible = true;
    });
    let emptyMsg = content.querySelector<HTMLElement>("#no-match");
    if (!anyVisible && term) {
      if (!emptyMsg) {
        emptyMsg = document.createElement("div");
        emptyMsg.id = "no-match";
        emptyMsg.className = "empty";
        content.appendChild(emptyMsg);
      }
      emptyMsg.textContent = `No platform matches "${searchInput.value.trim()}".`;
    } else if (emptyMsg) {
      emptyMsg.remove();
    }
  }

  clientSelect.onchange = () => void load();
  searchInput.oninput = () => applyFilter();
  await load();
}
